/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useRef } from 'react';
import { deepCopy } from '../general/Utils';
import { httpClient } from '../services/http/httpClient';
import { generateFilter, resetAllFilters } from '../filtering/Utils';
import { useEffectUpdate } from '../hooks';
export var exampleBoard = {
    zones: {
        'zoneIdOne': {
            components: {
                'componentIdOne': {
                    data: {
                        filter: {
                            and_: {
                                'attributeId': {
                                    eq: {
                                        value: 'hello',
                                        negate: true
                                    }
                                }
                            }
                        },
                        defaultFilter: {
                            and_: {
                                'attributeId': {
                                    eq: {
                                        value: 'hello',
                                        negate: true
                                    }
                                }
                            }
                        }
                    },
                }
            },
            order: ['componentIdOne'],
            type: 'species'
        },
    },
    order: ['zoneIdOne']
};
export function defineComponent(component, zone) {
    // setting default as empty if no filter provided
    var empty = { and_: {} };
    var filter = component.filter === undefined ? empty : component.filter;
    zone.components[component.id] = {
        data: __assign({ filter: deepCopy(filter), defaultFilter: deepCopy(filter) }, component)
    };
}
export function defineZone(objectType, components) {
    var zone = {
        components: {},
        order: [],
        type: objectType,
        filter: undefined,
        defaultFilter: undefined
    };
    for (var _i = 0, components_1 = components; _i < components_1.length; _i++) {
        var component = components_1[_i];
        defineComponent(component, zone);
        zone.order.push(component.id);
    }
    return zone;
}
export function useZone(params) {
    var endpoint = params.endpoint, baseUrl = params.baseUrl, components = params.components;
    var _a = useState(defineZone(endpoint, components)), zone = _a[0], setZone = _a[1];
    return {
        endpoint: endpoint,
        baseUrl: baseUrl,
        zone: zone,
        setZone: setZone
    };
}
export function generateTranslatedFilter(source, translations, excludeAfterId) {
    var sourceFilter = generateFilter(source.zone, excludeAfterId, true);
    var translatedFilter = { and_: {} };
    Object.entries(translations).map(function (_a) {
        var sourceAttribute = _a[0], targetAttribute = _a[1];
        if (sourceAttribute in sourceFilter.and_) {
            translatedFilter.and_[targetAttribute] = sourceFilter.and_[sourceAttribute];
        }
    });
    return translatedFilter;
}
export function useTranslator(params) {
    var source = params.source, target = params.target, translations = params.translations, defaultFilter = params.defaultFilter, excludeAfterId = params.excludeAfterId;
    var prevFilter = useRef(defaultFilter ? defaultFilter : { and_: {} });
    useEffectUpdate(function () {
        var translatedFilter = generateTranslatedFilter(source, translations, excludeAfterId);
        if (JSON.stringify(translatedFilter) !== JSON.stringify(prevFilter.current)) {
            resetAllFilters(target.zone);
            target.zone.filter = translatedFilter;
            target.setZone(__assign({}, target.zone));
            prevFilter.current = translatedFilter;
        }
    }, [source.zone]);
}
export function getWidgetOrder(layout, widgets) {
    // Sort the layout array by the 'y' property (and 'x' property in case of a tie)
    layout.sort(function (a, b) { return a.y - b.y || a.x - b.x; });
    // Map the sorted layout array to an array of widget objects
    var widgetOrder = layout.map(function (item) { return item.i; });
    return {
        components: widgets['components'],
        order: widgetOrder
    };
}
function getObjectTypes(baseUrl) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, httpClient().get('/_config/attribute_types', {
                        baseURL: baseUrl
                    })];
                case 1:
                    res = _a.sent();
                    // @ts-ignore
                    return [2 /*return*/, Object.keys(res.data)];
            }
        });
    });
}
export function fetchObjectTypes(baseUrl) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getObjectTypes(baseUrl)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
